"use client";

import type { ImpactPartner, Media } from "@mw/shared/payload-types";

import { Image } from "@ui/components/core/Image";
import { ImageSizes } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { Search } from "lucide-react";
import { runAfterNextBrowserPaint } from "@utilities/afterNextPaint";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { Link } from "@ui/components/core/Link";
import { CategoryName, useGetCategoryLabel, useImpactPartnerSearch } from "./ImpactPartnerGridHooks";
import * as m from "motion/react-m";
import { CategoryCardList } from "@ui/components/core/CategoryCardList";
import { getLinkProps, isEntity } from "@utilities/payload";
import { getUrlDisplayName } from "@utilities/url";
import { LinkProps } from "@/i18n/navigation";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import ImpactDetailPopupLayout from "./ImpactDetailPopupLayout";

interface ImpactPartnerPopupProps {
  partners: ImpactPartner[];
  selectedPartner: ImpactPartner | null;
  setSelectedPartner: (partner: ImpactPartner | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ImpactPartnerPopup({
  partners,
  selectedPartner: activeSelectedPartner,
  setSelectedPartner,
  open,
  onOpenChange,
}: ImpactPartnerPopupProps) {
  const t = useTranslations("ClientKeys.Components.impactGrid");
  const isMobile = useIsMobile();
  const selectedPartner = !isMobile && !activeSelectedPartner ? partners[0] : activeSelectedPartner;

  const { searchQuery, setSearchQuery, partnersByCategory } = useImpactPartnerSearch(partners);
  const getCategoryLabel = useGetCategoryLabel();

  // Scroll to selected partner when it changes or when dialog opens
  useEffect(() => {
    if (open && selectedPartner && !isMobile) {
      runAfterNextBrowserPaint(() => {
        const element = document.getElementById(selectedPartner.id);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      });
    }
  }, [selectedPartner, open, isMobile]);

  const infoItems = selectedPartner
    ? [
        ...(selectedPartner.diagnosisType
          ? [
              {
                label: t("info_type"),
                value: selectedPartner.diagnosisType,
              },
            ]
          : []),
        {
          label: t("info_category"),
          value: getCategoryLabel(selectedPartner.category),
        },
        {
          label: t("info_application"),
          value: selectedPartner.type === "application" ? t("application_partner") : t("application_none"),
        },
        {
          label: t("info_website"),
          value: getUrlDisplayName(selectedPartner.website),
          href: selectedPartner.website,
        },
      ]
    : [];

  const leftPanel = (
    <>
      <div className='relative shrink-0 px-20 pb-8 pt-32'>
        <div className='flex items-center gap-12 rounded-lg px-16'>
          <Search className='size-16 -scale-x-100 text-light' />
          <input
            className='f-paragraph-02 flex-1 bg-dark py-12 text-default outline-none'
            placeholder={t("search_partners")}
            type='text'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <DottedBorder side='bottom' />
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto'>
        {Object.entries(partnersByCategory).map(([category, categoryPartners]) => (
          <div key={category} className='relative'>
            <DottedBorder side='bottom' />
            <h5 className='f-ui-02 w-full px-32 py-16 text-left text-light'>
              {getCategoryLabel(category as CategoryName)}
            </h5>
            <div className='space-y-4 px-20 pb-16'>
              {categoryPartners.map(partner => (
                <button
                  id={partner.id}
                  key={partner.id}
                  className={cn(
                    "effect-focus relative flex w-full items-center gap-12 rounded-lg border border-transparent p-12 text-left transition-colors hover:bg-overlay-dark",
                    selectedPartner?.id === partner.id && "border-default",
                  )}
                  type='button'
                  onClick={() => setSelectedPartner(partner)}
                >
                  {selectedPartner?.id === partner.id && (
                    <m.div
                      layoutId='partner-background'
                      className='absolute inset-0 rounded-lg bg-default'
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 35,
                      }}
                    />
                  )}
                  <div className='relative z-10 size-24 flex-shrink-0'>
                    <Image
                      resource={partner.logo as Media}
                      className='h-full w-full object-contain'
                      sizes={ImageSizes.FULL_1_3_ON_LG}
                    />
                  </div>
                  <span className='f-ui-03 relative z-10 line-clamp-1 text-default'>{partner.name}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const rightPanel = selectedPartner ? (
    <div className='flex flex-col px-24 md:px-48'>
      <div className='my-32 flex size-92 justify-center rounded-xl bg-dark p-16'>
        <Image
          resource={selectedPartner.logo as Media}
          className='size-full object-contain'
          sizes={ImageSizes.FULL_1_3_ON_LG}
          height={60}
          width={60}
        />
      </div>
      <h3 className='f-heading-04 mb-8 text-left text-default'>{selectedPartner.name}</h3>
      <p className='f-paragraph-02 mb-16 text-left text-light'>{selectedPartner.description}</p>
      <div className='mb-16 space-y-0 lg:mb-48'>
        {infoItems.map(item => (
          <div key={item.label} className='relative flex items-center justify-between py-16'>
            <DottedBorder side='top' />
            <p className='f-paragraph-03 text-light'>{item.label}</p>
            {"href" in item && item.href ? (
              <Link
                href={item.href}
                className='f-paragraph-02 text-right text-default'
                rel='noopener noreferrer'
                target='_blank'
                variant='clean'
              >
                {item.value}
              </Link>
            ) : (
              <p className='f-paragraph-02 text-right capitalize text-default'>{item.value}</p>
            )}
          </div>
        ))}
      </div>
      {selectedPartner?.useCases?.length ? (
        <CategoryCardList
          className='hidden md:block'
          items={selectedPartner.useCases.map(p => {
            const { title, image, link } = p;
            const _image = isEntity(image)
              ? {
                  resource: image,
                }
              : undefined;
            return (
              <Link key={p.id} {...(getLinkProps(link) as LinkProps)} className='group relative cursor-pointer'>
                <div className='flex max-w-sm items-center gap-x-8'>
                  <Image className='aspect-1x1 size-60 rounded-md' sizes={ImageSizes.CONTENT_CARDS} {..._image} />
                  <div className='space-y-2 truncate'>
                    <div className='f-paragraph-03 flex items-center gap-x-4 capitalize text-light'>{t("story")}</div>
                    <h3 className='f-paragraph-02 truncate capitalize text-default'>{title}</h3>
                  </div>
                </div>
              </Link>
            );
          })}
        />
      ) : null}
    </div>
  ) : null;

  return (
    <ImpactDetailPopupLayout
      open={open}
      onOpenChange={onOpenChange}
      dialogTitle={selectedPartner?.name ?? ""}
      hasSelection={!!selectedPartner}
      leftPanel={leftPanel}
      rightPanel={rightPanel}
      onBack={() => setSelectedPartner(null)}
      backButtonLabel={t("back_to_list")}
    />
  );
}
